import { memo } from 'react';
import { Select } from 'antd';
import { CaretDownFilled, CloseOutlined } from '@ant-design/icons';
import type { WorkOrderFilterCategory, WorkOrderFilterCondition, WorkOrderFilterJoin } from '../../../../enums/workOrderFilter';
import type { WorkOrderFilterRule } from '../../../../types/InfrastructureManagerTypes';
import { DATE_FILTER_CATEGORIES, FILTER_CATEGORY_OPTIONS, FILTER_CONDITION_OPTIONS, FILTER_JOIN_OPTIONS } from '../constants';
import FilterDateValue from './FilterDateValue';
import { useFilterValueOptions } from './useFilterValueOptions';
import styles from './WorkOrderFilter.module.scss';

const SELECT_CARET = <CaretDownFilled />;
const REMOVE_ICON = <CloseOutlined aria-hidden />;
const NO_VALUE_OPTIONS: { value: string; label: string }[] = [];
const EMPTY_RANGE: [string, string] = ['', ''];

interface FilterRuleRowProps {
  rule: WorkOrderFilterRule;
  isFirst: boolean;
  onChange: (id: string, patch: Partial<WorkOrderFilterRule>) => void;
  onRemove: (id: string) => void;
}

const FilterRuleRow = ({ rule, isFirst, onChange, onRemove }: FilterRuleRowProps) => {
  const isDateCategory = rule.category ? DATE_FILTER_CATEGORIES.has(rule.category) : false;
  const valueOptions = useFilterValueOptions();

  const handleJoinChange = (join: WorkOrderFilterJoin) => onChange(rule.id, { join });
  const handleCategoryChange = (category: WorkOrderFilterCategory) => onChange(rule.id, { category, value: undefined });
  const handleConditionChange = (condition: WorkOrderFilterCondition) => onChange(rule.id, { condition });
  const handleValueChange = (value: string | [string, string]) => onChange(rule.id, { value });
  const handleRemove = () => onRemove(rule.id);

  return (
    <div className={styles.filterRule}>
      {isFirst ? (
        <span className={styles.filterWhere}>Where:</span>
      ) : (
        <Select
          className={styles.filterSelect}
          popupClassName={styles.filterSelectPopup}
          variant="borderless"
          suffixIcon={SELECT_CARET}
          aria-label="Join with previous filter"
          value={rule.join}
          onChange={handleJoinChange}
          options={FILTER_JOIN_OPTIONS}
        />
      )}
      <Select
        className={styles.filterSelect}
        popupClassName={styles.filterSelectPopup}
        variant="borderless"
        suffixIcon={SELECT_CARET}
        placeholder="Category"
        aria-label="Filter category"
        value={rule.category}
        onChange={handleCategoryChange}
        options={FILTER_CATEGORY_OPTIONS}
      />
      <Select
        className={styles.filterSelect}
        popupClassName={styles.filterSelectPopup}
        variant="borderless"
        suffixIcon={SELECT_CARET}
        placeholder="Condition"
        aria-label="Filter condition"
        value={rule.condition}
        onChange={handleConditionChange}
        options={FILTER_CONDITION_OPTIONS}
      />
      {isDateCategory ? (
        <FilterDateValue value={Array.isArray(rule.value) ? rule.value : EMPTY_RANGE} onChange={handleValueChange} />
      ) : (
        <Select
          className={styles.filterSelect}
          popupClassName={styles.filterSelectPopup}
          variant="borderless"
          suffixIcon={SELECT_CARET}
          placeholder="Value"
          aria-label="Filter value"
          value={typeof rule.value === 'string' ? rule.value : undefined}
          onChange={handleValueChange}
          options={(rule.category && valueOptions[rule.category]) || NO_VALUE_OPTIONS}
          notFoundContent="No values"
          disabled={!rule.category}
        />
      )}
      <button type="button" className={styles.filterRemove} aria-label="Remove filter row" onClick={handleRemove}>
        {REMOVE_ICON}
      </button>
    </div>
  );
};

export default memo(FilterRuleRow);
